/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $wrapNodeInElement, mergeRegister } from "@lexical/utils";
import {
    $createParagraphNode,
    $createRangeSelection,
    $createTextNode,
    $getNodeByKey,
    $getSelection,
    $insertNodes,
    $isNodeSelection,
    $isRangeSelection,
    $isRootOrShadowRoot,
    $isTextNode,
    $setSelection,
    COMMAND_PRIORITY_EDITOR,
    COMMAND_PRIORITY_HIGH,
    COMMAND_PRIORITY_LOW,
    createCommand,
    DRAGOVER_COMMAND,
    DRAGSTART_COMMAND,
    DROP_COMMAND,
    LexicalCommand,
    LexicalEditor
} from "lexical";
import { useCallback, useEffect, useRef, useState } from "react";
import { CAN_USE_DOM } from "../../utils/canUseDOM";

import { $createDocumentNode, $isDocumentNode, DocumentNode, DocumentPayload } from "./DocumentNode";
import { useLexicalEditorContainer } from "components/lexical/utils/lexicalUtils";
import { createPortal } from "react-dom";
import React from "react";
import { getLastWord } from "utils/Utils";
import { KnowledgeDocument, useFilteredDocuments } from "state/api/collections";
import { useCurrentCollectionId } from "state/GeneralSlice";

export type InsertDocumentPayload = Readonly<DocumentPayload>;
export type DeleteDocumentPayload = Readonly<{ nodeKey: string }>;

const getDOMSelection = (targetWindow: Window | null): Selection | null => (CAN_USE_DOM ? (targetWindow || window).getSelection() : null);

export const INSERT_DOCUMENT_COMMAND: LexicalCommand<InsertDocumentPayload> = createCommand("INSERT_DOCUMENT_COMMAND");
export const DELETE_DOCUMENT_COMMAND: LexicalCommand<DeleteDocumentPayload> = createCommand("DELETE_DOCUMENT_COMMAND");

export default function DocumentPlugin({ captionsEnabled }: { captionsEnabled?: boolean }): JSX.Element | null {
    const [editor] = useLexicalComposerContext();
    const tagPosition = useRef({ key: "", offest: 0 });

    const container = useLexicalEditorContainer();
    const [showPopup, setShowPopup] = useState(false);
    const [filter, setFilter] = useState("");
    const insertDocument = useCallback(
        (doc: KnowledgeDocument) => {
            editor.dispatchCommand(INSERT_DOCUMENT_COMMAND, {
                uuid: doc.filename,
                title: doc.title
            });
        },
        [editor]
    );

    useEffect(() => {
        const regex = /^@.{1,}/;

        return editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => {
                const selection = $getSelection();
                if ($isRangeSelection(selection)) {
                    const anchor = selection.anchor;
                    const focus = selection.focus;
                    if (anchor.key === focus.key && anchor.offset === focus.offset) {
                        const node = $getNodeByKey(anchor.key);
                        const text = node.getTextContent();
                        const word = getLastWord(text, anchor.offset);

                        if (regex.test(word)) {
                            if (!showPopup) {
                                tagPosition.current = { key: anchor.key, offest: text.indexOf(word) };
                                setShowPopup(true);
                            }
                            setFilter(word.substring(1));
                        } else {
                            setShowPopup(false);
                        }
                    }
                }
            });
        });
    }, [editor, showPopup]);

    useEffect(() => {
        if (!editor.hasNodes([DocumentNode])) {
            throw new Error("DocumentPlugin: DocumentNode not registered on editor");
        }

        return mergeRegister(
            editor.registerCommand<InsertDocumentPayload>(
                INSERT_DOCUMENT_COMMAND,
                (payload) => {
                    const documentNode = $createDocumentNode(payload);
                    $insertNodes([$createTextNode(" "), documentNode, $createTextNode(" ")]);
                    if ($isRootOrShadowRoot(documentNode.getParentOrThrow())) {
                        $wrapNodeInElement(documentNode, $createParagraphNode).selectEnd();
                    }
                    const node = $getNodeByKey(tagPosition.current.key);
                    if ($isTextNode(node)) {
                        node.setTextContent(
                            node
                                .getTextContent()
                                .replace("@" + filter, "")
                                .trimEnd()
                        );
                    }
                    return true;
                },
                COMMAND_PRIORITY_EDITOR
            ),
            editor.registerCommand<DragEvent>(
                DRAGSTART_COMMAND,
                (event) => {
                    return onDragStart(event);
                },
                COMMAND_PRIORITY_HIGH
            ),
            editor.registerCommand<DragEvent>(
                DRAGOVER_COMMAND,
                (event) => {
                    return onDragover(event);
                },
                COMMAND_PRIORITY_LOW
            ),
            editor.registerCommand<DragEvent>(
                DROP_COMMAND,
                (event) => {
                    return onDrop(event, editor);
                },
                COMMAND_PRIORITY_HIGH
            )
        );
    }, [editor, filter]);

    const collId = useCurrentCollectionId();
    const {
        data: { documents, documentsCount }
    } = useFilteredDocuments(collId, 5, 1, filter, true, "title", false, undefined);

    return (
        showPopup &&
        createPortal(
            <div className="absolute bottom-full translate-y-1 bg-blue-light px-2 py-1 border-2 rounded-md border-gray ">
                {documentsCount > 0 ? (
                    documents.map((doc) => (
                        <button
                            key={doc.filename}
                            onClick={() => insertDocument(doc)}
                            type="button"
                            className="block italic my-1 hover:bg-blue-dark w-full text-left"
                        >
                            {doc.title}.{doc.filetype}
                        </button>
                    ))
                ) : (
                    <>No documents...</>
                )}
            </div>,
            container
        )
    );
}

const TRANSPARENT_IMAGE = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
const img = document.createElement("img");
img.src = TRANSPARENT_IMAGE;

function onDragStart(event: DragEvent): boolean {
    const node = getDocumentNodeInSelection();
    if (!node) {
        return false;
    }
    const dataTransfer = event.dataTransfer;
    if (!dataTransfer) {
        return false;
    }
    dataTransfer.setData("text/plain", "_");
    dataTransfer.setDragImage(img, 0, 0);
    dataTransfer.setData(
        "application/x-lexical-drag",
        JSON.stringify({
            data: {
                text: node.__text,
                src: node.__src,
                key: node.getKey()
            },
            type: "document"
        })
    );

    return true;
}

function onDragover(event: DragEvent): boolean {
    const node = getDocumentNodeInSelection();
    if (!node) {
        return false;
    }
    if (!canDropDocument(event)) {
        event.preventDefault();
    }
    return true;
}

function onDrop(event: DragEvent, editor: LexicalEditor): boolean {
    const node = getDocumentNodeInSelection();
    if (!node) {
        return false;
    }
    const data = getDragDocumentData(event);
    if (!data) {
        return false;
    }
    event.preventDefault();
    if (canDropDocument(event)) {
        const range = getDragSelection(event);
        node.remove();
        const rangeSelection = $createRangeSelection();
        if (range !== null && range !== undefined) {
            rangeSelection.applyDOMRange(range);
        }
        $setSelection(rangeSelection);
        editor.dispatchCommand(INSERT_DOCUMENT_COMMAND, data);
    }
    return true;
}

function getDocumentNodeInSelection(): DocumentNode | null {
    const selection = $getSelection();
    if (!$isNodeSelection(selection)) {
        return null;
    }
    const nodes = selection.getNodes();
    const node = nodes[0];
    return $isDocumentNode(node) ? node : null;
}

function getDragDocumentData(event: DragEvent): null | InsertDocumentPayload {
    const dragData = event.dataTransfer?.getData("application/x-lexical-drag");
    if (!dragData) {
        return null;
    }
    const { type, data } = JSON.parse(dragData);
    if (type !== "document") {
        return null;
    }

    return data;
}

declare global {
    interface DragEvent {
        rangeOffset?: number;
        rangeParent?: Node;
    }
}

function canDropDocument(event: DragEvent): boolean {
    const target = event.target;
    return !!(
        target &&
        target instanceof HTMLElement &&
        !target.closest("code, span.editor-document") &&
        target.parentElement &&
        target.parentElement.closest("div.ContentEditable__root")
    );
}

function getDragSelection(event: DragEvent): Range | null | undefined {
    let range;
    const target = event.target as null | Element | Document;
    const targetWindow =
        target == null ? null : target.nodeType === 9 ? (target as Document).defaultView : (target as Element).ownerDocument.defaultView;
    const domSelection = getDOMSelection(targetWindow);
    if (document.caretRangeFromPoint) {
        range = document.caretRangeFromPoint(event.clientX, event.clientY);
    } else if (event.rangeParent && domSelection !== null) {
        domSelection.collapse(event.rangeParent, event.rangeOffset || 0);
        range = domSelection.getRangeAt(0);
    } else {
        throw Error(`Cannot get the selection when dragging`);
    }

    return range;
}
